import React from 'react';
import './rush.css';
import './features.css';
import './feature-detail.css';
import { RushNav, RushFooter, useRushReveal } from './RushChrome';
import { FI, FeatHero, FeatCapabilities, FeatDetailRow, FeatSteps, FeatQuote, FeatCrossLinks, FeatFinalCTA } from './RushFeatureLayout';

const PV_RISK = '#d2604f';

const PVHeroVisual = () => (
  <div className="mock-frame">
    <div className="mock-chrome"><span className="mc-dot"></span><span className="mc-dot"></span><span className="mc-dot"></span><span className="mc-title">In-context · DE · 1 issue</span></div>
    <div style={{ padding: 18 }}>
      <div style={{ border: '1px solid var(--line)', borderRadius: 12, overflow: 'hidden', background: 'var(--bg-card)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderBottom: '1px solid var(--line)', background: 'var(--bg-soft)' }}>
          <b style={{ fontSize: 13 }}>Acme</b>
          <span style={{ fontSize: 11.5, color: 'var(--ink-4)', marginLeft: 'auto' }}>Funktionen · Preise · Anmelden</span>
        </div>
        <div style={{ padding: '28px 18px 26px', textAlign: 'center' }}>
          <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-.02em', marginBottom: 8 }}>Veröffentlichen Sie<br />in jeder Sprache.</div>
          <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginBottom: 18, maxWidth: 280, margin: '0 auto 18px' }}>Die KI entwirft, Ihr Team verfeinert.</div>
          <span style={{ position: 'relative', display: 'inline-block', maxWidth: 168, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 12.5, fontWeight: 600, color: '#fff', background: 'var(--accent)', padding: '9px 18px', borderRadius: 9, outline: `2px solid ${PV_RISK}`, outlineOffset: 2 }}>Kostenlose Testversion starten</span>
          <div style={{ fontSize: 11, color: PV_RISK, fontWeight: 600, marginTop: 12, display: 'inline-flex', alignItems: 'center', gap: 6 }}>{FI.alert} Button text overflows by 34px</div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6, marginTop: 14 }}>
        {[['Web', true], ['Mobile', false], ['Email', false]].map(([l, on]) => (
          <span key={l} style={{ fontSize: 11.5, fontWeight: 600, padding: '5px 12px', borderRadius: 8, border: '1px solid var(--line)', background: on ? 'var(--ink)' : 'var(--bg-soft)', color: on ? 'var(--bg)' : 'var(--ink-3)' }}>{l}</span>
        ))}
        <span style={{ marginLeft: 'auto', fontSize: 11.5, fontWeight: 600, color: PV_RISK, background: '#fbe1e1', padding: '5px 11px', borderRadius: 8, display: 'inline-flex', alignItems: 'center', gap: 6 }}>{FI.alert} 1 layout issue</span>
      </div>
    </div>
  </div>
);

const PVStringsVisual = () => (
  <div className="mock-frame">
    <div className="mock-chrome"><span className="mc-dot"></span><span className="mc-dot"></span><span className="mc-dot"></span><span className="mc-title">Strings on screen · 9</span></div>
    <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {[['cta.trial', 'Start free trial', 'Kostenlose Testversion starten', false],
      ['hero.title', 'Ship in every language.', 'Veröffentlichen Sie in jeder Sprache.', true],
      ['nav.signin', 'Sign in', 'Anmelden', true],
      ['cta.primary', 'Get started', 'Jetzt loslegen', true]].map(([id, src, tgt, fits]) => (
        <div key={id} style={{ border: '1px solid var(--line)', borderLeft: `3px solid ${fits ? 'var(--green)' : PV_RISK}`, borderRadius: 9, padding: '9px 12px', background: 'var(--bg-soft)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-3)', fontFamily: 'ui-monospace, monospace' }}>{id}</span>
            <span style={{ marginLeft: 'auto', fontSize: 9.5, fontWeight: 700, padding: '2px 8px', borderRadius: 999, color: fits ? 'var(--green)' : PV_RISK, background: fits ? 'var(--green-soft)' : '#fbe1e1', display: 'inline-flex', alignItems: 'center', gap: 4 }}>{fits ? FI.check : FI.alert}{fits ? 'Fits' : 'Overflows'}</span>
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--ink)' }}>{tgt}</div>
          <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 2 }}>{src}</div>
        </div>
      ))}
    </div>
  </div>
);

const RushFeaturePreview = () => {
  useRushReveal();
  return (
    <div className="rs">
      <RushNav active="features" />
      <FeatHero
        tint="yellow"
        kicker="In-context preview"
        kickerIcon={FI.eye}
        title="Catch broken layouts before they ship."
        lead="See translations rendered in the actual UI — web, mobile, and email — so you catch the #1 localization embarrassment before launch. Rush Studio measures every string live and flags anything that overflows or clips its container."
        primary={{ label: 'Get early access', to: '/register' }}
        secondary={{ label: 'See all features', to: '/features' }}
        meta={['Web · mobile · email', 'Live overflow detection', 'Fix in place']}
        visual={<PVHeroVisual />}
      />

      <FeatCapabilities
        tint="yellow"
        eyebrow="What it does"
        title="Your copy, in the real layout."
        lead="No more shipping a translation and discovering the button clipped in production. Rush Studio renders the actual UI and watches every container."
        caps={[
          { icon: FI.eye, title: 'Real UI rendering', desc: 'Your copy in a real page, not an abstract string table you have to imagine.' },
          { icon: FI.alert, title: 'Live overflow detection', desc: 'Every string measured on render; overflows outlined in red with the pixels over.' },
          { icon: FI.device, title: 'Device & email views', desc: 'Switch between web, mobile, and email to catch the tightest containers.' },
          { icon: FI.ai, title: 'Fix in place', desc: 'Shorten a string and watch the warning clear in real time — no redeploy.' },
        ]}
      />

      <FeatDetailRow
        reverse
        eyebrow="Every string, measured"
        title="See exactly which strings break."
        intro="The panel lists every string on the screen with a live fit/overflow status. Click any flagged string to jump to it in the rendered UI, shorten it, and watch the warning clear instantly. Catch the long German compound before your customer does."
        points={[
          { icon: FI.alert, title: 'Per-string status', desc: 'A clear Fits / Overflows badge on every string, recalculated on every edit.' },
          { icon: FI.device, title: 'Tightest-container testing', desc: 'Mobile and email views surface the breaks that web alone would hide.' },
          { icon: FI.search, title: 'Jump & fix', desc: 'Click a flagged string to highlight it in context and edit it on the spot.' },
        ]}
        replaces={['Shipping and praying', 'Screenshots in review threads']}
        visual={<PVStringsVisual />}
      />

      <FeatSteps
        eyebrow="How it works"
        title="From translated string to safe launch."
        steps={[
          { h: 'Render the real UI', p: 'Rush Studio drops your translated strings into the actual web, mobile, and email layouts.' },
          { h: 'Measure every container', p: 'Each string is measured on render; anything that overflows or clips is outlined in red.' },
          { h: 'Fix and confirm', p: 'Shorten the offending string in place and watch the warning clear — before anything ships.' },
        ]}
      />

      <FeatQuote quote="Our German launch used to mean a frantic round of layout bug reports. Now we see the overflow before the PR even merges." initials="DP" name="Dani Park" role="Design ops, Quanta" />

      <FeatCrossLinks current="preview" />

      <FeatFinalCTA source="feature-preview" title="Never ship a broken layout again." sub="Join the waitlist for early-access pricing and a 1:1 onboarding call with our team." />

      <RushFooter />
    </div>
  );
};

export default RushFeaturePreview;
